import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type FC,
  type ReactElement,
  type DragEvent
} from 'react';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCog,
  faDiceD20,
  faBookOpen,
  faBook,
  faScroll,
  faTimes,
  faQuestionCircle,
  faMoon,
  faSun,
  faRightFromBracket,
  faRobot,
  faSearch,
  faAnglesLeft,
  faAnglesRight,
  faUserPlus,
  faStar,
  faGripVertical,
  faTrash,
  faChevronDown,
  faPlus,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import {
  Link,
  Separator,
  Button,
  Avatar,
  Tooltip,
  Modal,
  StandardModalHeader
} from 'src/design-system';
import { trackEvent } from 'src/utils/analytics';
import { useTheme } from 'src/design-system';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppSelector } from 'store/hooks';
import { selectUserName } from 'store/user/selectors';
import { clearTokenInfo } from 'src/utils/authUtils';
import { resetCrispSession } from 'src/utils/crisp';
import { clearImpersonationStorage } from 'src/store/user/userSlice';
import GlobalSearch from './GlobalSearch';
import { UserSwitcher } from './admin/UserSwitcher';
import ReferralPanel from './referrals/ReferralPanel';
import CompendiumBetaBadge from 'src/Pages/Compendium/components/shared/CompendiumBetaBadge';
import { useFavorites } from 'src/contexts/FavoritesContext';
import { UserFavorite } from 'src/api/types/favorite';
import SessionsApi from 'src/api/sessions';
import { Session } from 'src/api/types/session';

const STORAGE_KEY = 'sidemenu-collapsed';

interface MenuItem {
  name: string;
  path?: string;
  icon: IconDefinition;
  onClick?: () => void;
  showOnMobile?: boolean;
}

interface SideMenuProps {
  isOpen?: boolean;
  onClose?: () => void;
  onShowCrisp?: () => void;
  isMobile?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

function CollapsedNavTooltip({
  label,
  isCollapsed,
  children
}: {
  label: string;
  isCollapsed: boolean;
  children: ReactElement;
}) {
  if (!isCollapsed) {
    return children;
  }

  return (
    <Tooltip delay={100}>
      <Tooltip.Trigger className="flex w-full">{children}</Tooltip.Trigger>
      <Tooltip.Content placement="right" showArrow>
        <Tooltip.Arrow />
        {label}
      </Tooltip.Content>
    </Tooltip>
  );
}

const SUPPORT_URL = 'https://support.thedmsark.com/en/';
const COMPENDIUM_MENU_ITEM_NAME = 'Compendium';
const PENDING_FAVORITE_PREFIX = 'pending-';
const menuItems: MenuItem[] = [
  {
    name: 'Campaigns',
    path: '/campaigns',
    icon: faBookOpen
  },
  {
    name: 'Sessions',
    path: '/sessions',
    icon: faDiceD20
  },
  {
    name: COMPENDIUM_MENU_ITEM_NAME,
    path: '/compendium',
    icon: faScroll
  },
  // { name: 'ScribeAI', path: '/scribe-ai', icon: faBook },
  {
    name: 'Settings',
    path: '/settings',
    icon: faCog
  },
  {
    name: 'Help',
    icon: faQuestionCircle,
    onClick: () => window.open(SUPPORT_URL, '_blank')
  },
  {
    name: 'Add Discord Bot',
    icon: faRobot,
    onClick: () => {
      trackEvent('Add Bot Clicked', {
        location: 'side_menu'
      });
      window.open('https://discord.com/oauth2/authorize?client_id=1328480794112426034', '_blank');
    }
  },
  {
    name: 'Join our Discord',
    icon: faDiscord,
    onClick: () => {
      trackEvent('Join Discord Clicked', {
        location: 'side_menu'
      });
      window.open('https://discord.gg/9NDYNcd7eF', '_blank');
    }
  }
];

const favoriteIcon = (favorite: UserFavorite) => {
  switch (favorite.targetType) {
    case 'campaign':
      return faBookOpen;
    case 'session':
      return faDiceD20;
    case 'compendium':
      return faBook;
    case 'compendium_page':
      return faScroll;
    default:
      return faStar;
  }
};

interface CampaignSessionsState {
  sessions: Session[];
  page: number;
  hasMore: boolean;
  loading: boolean;
  loaded: boolean;
}

const EMPTY_CAMPAIGN_SESSIONS: CampaignSessionsState = {
  sessions: [],
  page: 0,
  hasMore: false,
  loading: false,
  loaded: false
};

const FavoriteCampaignSessions: FC<{
  campaignId: string;
  data: CampaignSessionsState;
  onLoadMore: () => void;
  onNavigate?: () => void;
}> = ({ campaignId, data, onLoadMore, onNavigate }) => {
  const { sessions, loading, hasMore } = data;

  return (
    <div className="ml-5 mt-1 space-y-1 border-l border-divider pl-2">
      <NavLink
        to={`/campaigns/${campaignId}/sessions/create`}
        onClick={onNavigate}
        className={({ isActive }) =>
          `flex min-h-7 items-center gap-2 rounded-md px-2 py-1 text-sm font-medium transition-colors ${
            isActive
              ? 'bg-default-100 text-primary dark:bg-white/10'
              : 'text-primary hover:bg-default-100 dark:hover:bg-white/10'
          }`
        }>
        <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">New Session</span>
      </NavLink>
      {sessions.map((session) => (
        <NavLink
          key={session.id}
          to={`/sessions/${session.id}/transcript`}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex min-h-7 items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors ${
              isActive
                ? 'bg-default-100 text-foreground dark:bg-white/10'
                : 'text-default-500 hover:bg-default-100 hover:text-foreground dark:hover:bg-white/10'
            }`
          }>
          <FontAwesomeIcon icon={faDiceD20} className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate">{session.title}</span>
        </NavLink>
      ))}
      {loading && sessions.length === 0 ? (
        <div className="px-2 py-1.5 text-xs text-default-400">Loading sessions...</div>
      ) : null}
      {hasMore ? (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={loading}
          className="w-full rounded-md px-2 py-1.5 text-left text-xs text-default-500 transition-colors hover:bg-accent/75 hover:text-accent-foreground disabled:opacity-60">
          {loading ? 'Loading...' : 'Load more'}
        </button>
      ) : null}
    </div>
  );
};

const SideMenu: FC<SideMenuProps> = ({
  isOpen = false,
  onClose,
  onShowCrisp,
  isMobile = false,
  onCollapsedChange
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isReferralModalOpen, setIsReferralModalOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : false;
  });
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const { theme, setTheme } = useTheme('dark');
  const logo = theme === 'dark' ? '/logo.svg' : '/logo-dark.svg';
  const { logout } = useAuth0();
  const userName = useAppSelector(selectUserName);
  const user = useAppSelector((state) => state.auth.user);
  const userPic = user?.discordAvatar ?? user?.googleAvatar;
  const { favorites, removeFavorite, updateFavorite, reorderFavorites } = useFavorites();
  // Snapshot favorites with expansion so list changes can be derived without setState-in-render.
  const [expansion, setExpansion] = useState(() => {
    const ids = new Set<string>();
    favorites.forEach((favorite) => {
      if (favorite.targetType === 'campaign' && favorite.includeChildren) {
        ids.add(favorite.id);
      }
    });
    return { favorites, ids };
  });
  const expandedFavoriteIds =
    expansion.favorites === favorites
      ? expansion.ids
      : (() => {
          const next = new Set<string>();
          favorites.forEach((favorite) => {
            if (
              expansion.ids.has(favorite.id) ||
              (favorite.targetType === 'campaign' && favorite.includeChildren)
            ) {
              next.add(favorite.id);
            }
          });
          return next;
        })();
  const [draggingFavoriteId, setDraggingFavoriteId] = useState<string | null>(null);
  const [dragOverFavoriteId, setDragOverFavoriteId] = useState<string | null>(null);
  const [campaignSessionsById, setCampaignSessionsById] = useState<
    Record<string, CampaignSessionsState>
  >({});
  const inFlightCampaignSessionRequestsRef = useRef<Set<string>>(new Set());

  const orderedFavorites = useMemo(
    () => [...favorites].sort((a, b) => a.position - b.position),
    [favorites]
  );

  const loadCampaignSessions = useCallback((campaignId: string, nextPage: number) => {
    const requestKey = `${campaignId}:${nextPage}`;
    if (inFlightCampaignSessionRequestsRef.current.has(requestKey)) {
      return;
    }
    inFlightCampaignSessionRequestsRef.current.add(requestKey);

    setCampaignSessionsById((current) => ({
      ...current,
      [campaignId]: { ...(current[campaignId] ?? EMPTY_CAMPAIGN_SESSIONS), loading: true }
    }));

    SessionsApi.fetchSessionsByCampaignId(
      campaignId,
      10,
      nextPage,
      'created_date',
      'side-menu-favorites'
    )
      .then((response) => {
        setCampaignSessionsById((current) => {
          const previous = current[campaignId] ?? EMPTY_CAMPAIGN_SESSIONS;
          const sessions =
            nextPage === 1 ? response.data.data : [...previous.sessions, ...response.data.data];
          return {
            ...current,
            [campaignId]: {
              sessions,
              page: nextPage,
              hasMore: response.data.hasMore,
              loading: false,
              loaded: true
            }
          };
        });
      })
      .catch(() => {
        setCampaignSessionsById((current) => ({
          ...current,
          [campaignId]: { ...(current[campaignId] ?? EMPTY_CAMPAIGN_SESSIONS), loading: false }
        }));
      })
      .finally(() => {
        inFlightCampaignSessionRequestsRef.current.delete(requestKey);
      });
  }, []);

  useEffect(() => {
    favorites.forEach((favorite) => {
      if (
        favorite.targetType === 'campaign' &&
        expandedFavoriteIds.has(favorite.id) &&
        !campaignSessionsById[favorite.targetId]?.loaded &&
        !campaignSessionsById[favorite.targetId]?.loading
      ) {
        loadCampaignSessions(favorite.targetId, 1);
      }
    });
  }, [expandedFavoriteIds, favorites, campaignSessionsById, loadCampaignSessions]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed));
    onCollapsedChange?.(isCollapsed);
  }, [isCollapsed, onCollapsedChange]);

  // Create dynamic menu items including Support on mobile
  const allMenuItems = useMemo(() => {
    const referralItem: MenuItem = {
      name: 'Refer A Friend',
      icon: faUserPlus,
      onClick: () => {
        trackEvent('Refer A Friend Clicked', {
          location: 'side_menu'
        });
        setIsReferralModalOpen(true);
      }
    };
    const items = [...menuItems.slice(0, 3), referralItem, ...menuItems.slice(3)];
    if (user?.isAdmin) {
      items.push({ name: 'Admin', path: '/admin', icon: faChartLine });
    }
    if (isMobile && onShowCrisp) {
      items.push({
        name: 'Chat with us',
        icon: faQuestionCircle,
        onClick: onShowCrisp,
        showOnMobile: true
      });
    }
    return items;
  }, [isMobile, onShowCrisp, user]);

  const handleItemClick = (item: MenuItem) => {
    if (item.onClick) {
      item.onClick();
    }
    if (onClose) {
      onClose();
    }
  };

  const handleLogout = () => {
    clearTokenInfo();
    clearImpersonationStorage();
    resetCrispSession();
    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/logout`
      }
    });
  };

  const handleFavoriteDragStart = (event: DragEvent<HTMLButtonElement>, favorite: UserFavorite) => {
    if (favorite.id.startsWith(PENDING_FAVORITE_PREFIX)) {
      event.preventDefault();
      return;
    }

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', favorite.id);
    setDraggingFavoriteId(favorite.id);
  };

  const handleFavoriteDragOver = (event: DragEvent<HTMLLIElement>, targetFavoriteId: string) => {
    const sourceFavoriteId = draggingFavoriteId ?? event.dataTransfer.getData('text/plain');
    if (!sourceFavoriteId || sourceFavoriteId === targetFavoriteId) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverFavoriteId(targetFavoriteId);
  };

  const handleFavoriteDrop = (event: DragEvent<HTMLLIElement>, targetFavoriteId: string) => {
    event.preventDefault();

    const sourceFavoriteId = draggingFavoriteId ?? event.dataTransfer.getData('text/plain');
    setDraggingFavoriteId(null);
    setDragOverFavoriteId(null);

    if (!sourceFavoriteId || sourceFavoriteId === targetFavoriteId) {
      return;
    }

    const currentIndex = orderedFavorites.findIndex((favorite) => favorite.id === sourceFavoriteId);
    const nextIndex = orderedFavorites.findIndex((favorite) => favorite.id === targetFavoriteId);
    if (currentIndex < 0 || nextIndex < 0) {
      return;
    }

    const nextOrder = [...orderedFavorites];
    const [moved] = nextOrder.splice(currentIndex, 1);
    if (!moved) return;
    nextOrder.splice(nextIndex, 0, moved);
    void reorderFavorites(nextOrder.map((favorite) => favorite.id));
  };

  const handleFavoriteDragEnd = () => {
    setDraggingFavoriteId(null);
    setDragOverFavoriteId(null);
  };

  const toggleExpandedFavorite = (favorite: UserFavorite) => {
    const willExpand = !expandedFavoriteIds.has(favorite.id);

    const next = new Set(expandedFavoriteIds);
    if (next.has(favorite.id)) {
      next.delete(favorite.id);
    } else {
      next.add(favorite.id);
    }
    setExpansion({ favorites, ids: next });

    if (favorite.targetType === 'campaign' && favorite.includeChildren !== willExpand) {
      void updateFavorite(favorite.id, { includeChildren: willExpand });
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (!isShortcut) {
        return;
      }

      const target = event.target as HTMLElement | null;
      const isTypingTarget =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable === true;

      if (isTypingTarget) {
        return;
      }

      event.preventDefault();
      setIsSearchOpen(true);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleSearchOpenChange = (open: boolean) => {
    setIsSearchOpen(open);

    if (!open) {
      requestAnimationFrame(() => {
        searchTriggerRef.current?.focus();
      });
    }
  };

  const navLinkClass = (isActive: boolean) =>
    `flex w-full items-center rounded-md p-2 transition-colors ${
      isCollapsed ? 'justify-center' : ''
    } ${
      isActive
        ? 'bg-accent text-accent-foreground'
        : 'hover:bg-accent/75 hover:text-accent-foreground'
    }`;

  const actionButtonClass = `flex w-full items-center rounded-md p-2 transition-colors hover:bg-accent hover:text-accent-foreground ${
    isCollapsed ? 'justify-center' : 'text-left'
  }`;

  const renderMenuItemLabel = (item: MenuItem, isActive: boolean) => {
    if (item.name !== COMPENDIUM_MENU_ITEM_NAME) {
      return item.name;
    }

    return (
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className="truncate">{item.name}</span>
        <CompendiumBetaBadge className="shrink-0" active={isActive} />
      </span>
    );
  };

  const favoriteLinkClass = (isActive: boolean, collapsed: boolean, mobile = false) =>
    `flex min-w-0 flex-1 items-center rounded-md transition-colors ${
      mobile ? 'px-3 py-2.5' : 'px-2 py-1.5'
    } ${collapsed ? 'justify-center' : ''} ${
      isActive
        ? 'bg-default-100 text-foreground dark:bg-white/10'
        : 'text-default-500 hover:bg-default-100 hover:text-foreground dark:hover:bg-white/10'
    }`;

  const renderFavoriteControls = (favorite: UserFavorite) => {
    const isPending = favorite.id.startsWith(PENDING_FAVORITE_PREFIX);

    return (
      <div className="flex shrink-0 items-center gap-0.5 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
        <button
          type="button"
          aria-label={`Remove ${favorite.label} from favorites`}
          disabled={isPending}
          onClick={() => void removeFavorite(favorite.id)}
          className="flex h-8 w-8 items-center justify-center rounded-md text-default-400 transition-colors hover:bg-danger-50 hover:text-danger disabled:opacity-50 dark:hover:bg-danger/15">
          <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  };

  const renderFavoriteRow = (favorite: UserFavorite, mobile = false) => {
    const isCampaignAccordion = favorite.targetType === 'campaign';
    const isExpanded = expandedFavoriteIds.has(favorite.id);
    const icon = favoriteIcon(favorite);
    const isDragging = draggingFavoriteId === favorite.id;
    const isDropTarget = dragOverFavoriteId === favorite.id && !isDragging;
    const isPending = favorite.id.startsWith(PENDING_FAVORITE_PREFIX);

    if (isCollapsed && !mobile) {
      return (
        <li key={favorite.id} className="w-full">
          <CollapsedNavTooltip label={favorite.label} isCollapsed={isCollapsed}>
            <NavLink
              to={favorite.path}
              aria-label={favorite.label}
              className={({ isActive }) => favoriteLinkClass(isActive, true)}>
              <FontAwesomeIcon icon={icon} className="h-5 w-5 shrink-0" />
            </NavLink>
          </CollapsedNavTooltip>
        </li>
      );
    }

    return (
      <li
        key={favorite.id}
        className={`w-full rounded-md transition-colors ${isDragging ? 'opacity-60' : ''} ${
          isDropTarget ? 'bg-default-100 dark:bg-white/10' : ''
        }`}
        onDragOver={(event) => handleFavoriteDragOver(event, favorite.id)}
        onDragLeave={() => setDragOverFavoriteId(null)}
        onDrop={(event) => handleFavoriteDrop(event, favorite.id)}>
        <div className="group flex min-w-0 items-center gap-1">
          <button
            type="button"
            aria-label={`Drag ${favorite.label} to reorder favorites`}
            draggable={!isPending}
            disabled={isPending}
            onDragStart={(event) => handleFavoriteDragStart(event, favorite)}
            onDragEnd={handleFavoriteDragEnd}
            className="flex h-8 w-5 shrink-0 cursor-grab items-center justify-center rounded-md text-default-300 transition-colors hover:bg-default-100 hover:text-default-500 active:cursor-grabbing disabled:cursor-default disabled:opacity-40 dark:hover:bg-white/10">
            <FontAwesomeIcon icon={faGripVertical} className="h-3.5 w-3.5" />
          </button>
          {isCampaignAccordion ? (
            <button
              type="button"
              aria-label={isExpanded ? 'Collapse sessions' : 'Expand sessions'}
              aria-expanded={isExpanded}
              onClick={() => toggleExpandedFavorite(favorite)}
              className="flex h-8 w-5 shrink-0 items-center justify-center rounded-md text-default-400 transition-colors hover:bg-default-100 hover:text-foreground dark:hover:bg-white/10">
              <FontAwesomeIcon
                icon={faChevronDown}
                className={`h-3 w-3 transition-transform ${isExpanded ? '' : '-rotate-90'}`}
              />
            </button>
          ) : null}
          <NavLink
            to={favorite.path}
            onClick={mobile ? onClose : undefined}
            className={({ isActive }) => favoriteLinkClass(isActive, false, mobile)}>
            <FontAwesomeIcon icon={icon} className="h-4 w-4 shrink-0 mr-2" />
            <span className="truncate text-sm font-medium">{favorite.label}</span>
          </NavLink>
          {renderFavoriteControls(favorite)}
        </div>
        {isCampaignAccordion && isExpanded ? (
          <FavoriteCampaignSessions
            campaignId={favorite.targetId}
            data={campaignSessionsById[favorite.targetId] ?? EMPTY_CAMPAIGN_SESSIONS}
            onLoadMore={() => {
              const current = campaignSessionsById[favorite.targetId];
              loadCampaignSessions(favorite.targetId, (current?.page ?? 0) + 1);
            }}
            onNavigate={mobile ? onClose : undefined}
          />
        ) : null}
      </li>
    );
  };

  const renderFavoritesSection = (mobile = false) => {
    if (orderedFavorites.length === 0) {
      return null;
    }

    return (
      <div className={mobile ? 'mb-4' : isCollapsed ? 'mb-3' : 'mb-4'}>
        {!isCollapsed || mobile ? (
          <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-default-400">
            Favorites
          </p>
        ) : null}
        <ul className="space-y-1">
          {orderedFavorites.map((favorite) => renderFavoriteRow(favorite, mobile))}
        </ul>
      </div>
    );
  };

  return (
    <>
      {/* Desktop sidebar - always visible on lg+ screens */}
      <aside
        className={`fixed left-0 top-0 z-30 hidden h-full flex-col border-r border-divider transition-all duration-300 lg:flex ${
          isCollapsed ? 'w-14' : 'w-72'
        }`}>
        <div
          className={
            isCollapsed
              ? 'flex flex-col items-center gap-1 px-1 py-2'
              : 'flex items-center justify-between gap-2 px-4 py-3'
          }>
          <Link
            href="https://thedmsark.com"
            className={`flex items-center no-underline ${isCollapsed ? 'justify-center' : 'gap-2'}`}>
            <img src={logo} alt="Logo" className="h-8 w-auto" />
            {!isCollapsed && <span className="text-sm font-bold">The DM&apos;s Ark</span>}
          </Link>

          <div className={`flex items-center ${isCollapsed ? 'flex-col gap-1' : 'gap-1'}`}>
            <CollapsedNavTooltip label="Search (⌘K)" isCollapsed={isCollapsed}>
              <Button
                ref={searchTriggerRef}
                isIconOnly
                size="sm"
                variant="ghost"
                onPress={() => setIsSearchOpen(true)}
                aria-label="Open global search">
                <FontAwesomeIcon icon={faSearch} />
              </Button>
            </CollapsedNavTooltip>
            <CollapsedNavTooltip
              label={theme === 'light' ? 'Dark mode' : 'Light mode'}
              isCollapsed={isCollapsed}>
              <Button
                isIconOnly
                size="sm"
                variant="ghost"
                onPress={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                aria-label="Toggle theme">
                <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
              </Button>
            </CollapsedNavTooltip>
          </div>
        </div>

        <Separator />

        <div className={`space-y-3 py-3 ${isCollapsed ? 'px-1' : 'px-4'}`}>
          <CollapsedNavTooltip label={userName ?? 'User'} isCollapsed={isCollapsed}>
            <div
              className={`flex w-full items-center rounded-xl p-0.5 ${
                isCollapsed ? 'justify-center' : 'gap-3'
              }`}>
              <Avatar size="md">
                {userPic ? <Avatar.Image alt={userName ?? 'User'} src={userPic} /> : null}
                <Avatar.Fallback>{(userName ?? 'U').slice(0, 1).toUpperCase()}</Avatar.Fallback>
              </Avatar>
              {!isCollapsed && (
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{userName}</p>
                </div>
              )}
            </div>
          </CollapsedNavTooltip>

          {user?.isAdmin && !isCollapsed && (
            <div>
              <UserSwitcher />
            </div>
          )}
        </div>

        <Separator />

        <nav className={`mt-3 flex-1 overflow-y-auto ${isCollapsed ? 'px-1' : 'mx-4'}`}>
          {renderFavoritesSection(false)}
          {orderedFavorites.length > 0 ? <Separator className="mb-3 opacity-60" /> : null}
          <ul className="space-y-2">
            {allMenuItems.map((item) => (
              <li key={item.path ?? `action-${item.name}`} className="w-full">
                {item.path ? (
                  <CollapsedNavTooltip label={item.name} isCollapsed={isCollapsed}>
                    <NavLink
                      to={item.path}
                      aria-label={isCollapsed ? item.name : undefined}
                      className={({ isActive }) => navLinkClass(isActive)}>
                      {({ isActive }) => (
                        <>
                          <FontAwesomeIcon
                            icon={item.icon}
                            className={`h-5 w-5 shrink-0 ${isCollapsed ? '' : 'mr-2'}`}
                          />
                          {!isCollapsed && renderMenuItemLabel(item, isActive)}
                        </>
                      )}
                    </NavLink>
                  </CollapsedNavTooltip>
                ) : (
                  <CollapsedNavTooltip label={item.name} isCollapsed={isCollapsed}>
                    <button
                      type="button"
                      onClick={() => handleItemClick(item)}
                      aria-label={isCollapsed ? item.name : undefined}
                      className={actionButtonClass}>
                      <FontAwesomeIcon
                        icon={item.icon}
                        className={`h-5 w-5 shrink-0 ${isCollapsed ? '' : 'mr-2'}`}
                      />
                      {!isCollapsed && item.name}
                    </button>
                  </CollapsedNavTooltip>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <Separator />

        <div className={`space-y-2 py-3 pb-24 ${isCollapsed ? 'px-1' : 'px-4'}`}>
          <CollapsedNavTooltip label="Log out" isCollapsed={isCollapsed}>
            <button
              type="button"
              onClick={handleLogout}
              aria-label={isCollapsed ? 'Log out' : undefined}
              className={`flex w-full items-center rounded-md px-2 py-2 text-sm text-danger transition-colors hover:bg-danger/10 ${
                isCollapsed ? 'justify-center' : 'gap-2 text-left'
              }`}>
              <FontAwesomeIcon icon={faRightFromBracket} className="h-4 w-4 shrink-0" />
              {!isCollapsed && 'Log out'}
            </button>
          </CollapsedNavTooltip>

          <Tooltip delay={500}>
            <Tooltip.Trigger className="flex w-full">
              <button
                type="button"
                onClick={() => setIsCollapsed(!isCollapsed)}
                aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
                className={`flex w-full items-center rounded-md px-2 py-2 text-sm transition-colors hover:bg-accent/75 hover:text-accent-foreground ${
                  isCollapsed ? 'justify-center' : 'text-left'
                }`}>
                <FontAwesomeIcon
                  icon={isCollapsed ? faAnglesRight : faAnglesLeft}
                  className="h-4 w-4 shrink-0"
                />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Content placement="right" showArrow>
              <Tooltip.Arrow />
              {isCollapsed ? 'Expand menu' : 'Collapse menu'}
            </Tooltip.Content>
          </Tooltip>
        </div>
      </aside>

      {/* Mobile sidebar - slide in from left */}
      <aside
        className={`
        lg:hidden fixed top-0 left-0 h-full w-64 border-r border-divider z-50
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between mx-4 my-1">
          <Link href="https://thedmsark.com" className="flex items-center">
            <img src={logo} alt="Logo" className="h-10 w-auto" />
          </Link>
          <div className="flex items-center gap-1">
            <Button
              isIconOnly
              size="sm"
              variant="ghost"
              onPress={() => setIsSearchOpen(true)}
              aria-label="Open global search">
              <FontAwesomeIcon icon={faSearch} />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="ghost"
              onPress={onClose}
              className="text-default-500 hover:text-default-700">
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          </div>
        </div>

        <Separator className="" />

        <nav className="flex-1 mx-4 mt-2 overflow-y-auto">
          {renderFavoritesSection(true)}
          {orderedFavorites.length > 0 ? <Separator className="mb-3 opacity-60" /> : null}
          <ul className="space-y-2">
            {allMenuItems.map((item) => (
              <li key={item.path ?? `action-${item.name}`}>
                {item.path ? (
                  <NavLink
                    to={item.path}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center p-3 rounded-md transition-colors ${
                        isActive ? 'bg-primary text-primary-foreground' : 'hover:opacity-80'
                      }`
                    }>
                    {({ isActive }) => (
                      <>
                        <FontAwesomeIcon icon={item.icon} className="w-5 h-5 mr-3" />
                        <span className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium">
                          {renderMenuItemLabel(item, isActive)}
                        </span>
                      </>
                    )}
                  </NavLink>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleItemClick(item)}
                    className="flex w-full items-center rounded-md p-3 text-left transition-colors hover:opacity-80">
                    <FontAwesomeIcon icon={item.icon} className="w-5 h-5 mr-3" />
                    <span className="text-sm font-medium">{item.name}</span>
                  </button>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <GlobalSearch isOpen={isSearchOpen} onOpenChange={handleSearchOpenChange} />

      <Modal.Backdrop
        isOpen={isReferralModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsReferralModalOpen(false);
          }
        }}>
        <Modal.Container size="lg">
          <Modal.Dialog>
            <StandardModalHeader
              title="Refer A Friend"
              description="Share your signup link. Earn 1 month free for each qualified friend."
              onClose={() => setIsReferralModalOpen(false)}
            />
            <Modal.Body className="pt-2">
              <ReferralPanel variant="plain" />
            </Modal.Body>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </>
  );
};
export default SideMenu;
